'use client';

import { RestaurantShift } from '@/lib/types/StoreType';
import * as React from 'react';
import SvgClock from '@ui/Icons/SvgClock';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { useTranslations } from 'next-intl';
import { ActiveSubscription } from '@/lib/types/customer/restaurant/ActiveSubscription';
import { isPaidSubscriber } from '@/lib/helpers/functions';
import SvgCheck from '@/components/ui/Icons/Check';
import { Close } from '@/components/ui/Icons';
import If from '@ui/If';
import {
  compareIfGreaterThen,
  parseTimeZoneDateWithoutFormat,
} from '@/lib/helpers/datetime';

type Props = {
  shifts: RestaurantShift[];
  activeSubscription: ActiveSubscription;
  isOpen: boolean;
  isOpeningSoon: boolean;
};

export const StoreOpeningHours = ({
  shifts,
  activeSubscription,
  isOpen,
  isOpeningSoon,
}: Props) => {
  const [isExpaned, setIsExpanded] = React.useState(false);

  const t = useTranslations('');

  const parseTimeWithTimeZone = (timeString: string): string => {
    let parsedTime = parseTimeZoneDateWithoutFormat(timeString);
    const hour = parsedTime.hour();
    const minute = parsedTime.minute();
    if (hour === 23 && minute === 59) {
      parsedTime = parsedTime.add(1, 'minute');
    }
    let formattedTime = parsedTime.format('HH:mm');
    if (formattedTime === '00:00') {
      formattedTime = '24:00';
    }
    return formattedTime;
  };

  const formattedTime = (time: string): string => {
    return parseTimeWithTimeZone(time);
  };

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  type DaysWorkingHours = { [key: string]: RestaurantShift[] };

  const formattedShifts: DaysWorkingHours = {};

  daysOfWeek.forEach((day) => {
    formattedShifts[day] = [];
    shifts.forEach((shift) => {
      if (shift.day === day) {
        formattedShifts[day].push(shift);
      }
    });
  });

  const sortWorkingHours = (hours: DaysWorkingHours) => {
    for (const day in hours) {
      hours[day].sort((a, b) => compareIfGreaterThen(a.start_utc, b.start_utc));
    }
    return hours;
  };

  const sortedWorkingHours = sortWorkingHours(formattedShifts);
  const iterations = daysOfWeek.length * 2;
  const formattedShiftsValues = Object.values(sortedWorkingHours);

  for (let i = 0; i < iterations; i++) {
    const currentIdx = i % daysOfWeek.length;
    const nextIdx = (i + 1) % daysOfWeek.length;
    const current = formattedShiftsValues[currentIdx];
    const next = formattedShiftsValues[nextIdx];
    const currentDay = daysOfWeek[currentIdx];
    const nextDay = daysOfWeek[nextIdx];

    let hour;
    let minute;
    let startHour;
    let startMinute;
    let endHour;
    const untilMidnightEndIdx = current.findIndex((item) => {
      const germanTime = parseTimeZoneDateWithoutFormat(item.end_utc);
      hour = germanTime.hour();
      minute = germanTime.minute();
      return hour === 23 && minute === 59;
    });

    const until4amIdx = next.findIndex((item) => {
      const parsedTimeStart = parseTimeZoneDateWithoutFormat(item.start_utc);
      const parsedTimeEnd = parseTimeZoneDateWithoutFormat(item.end_utc);
      startHour = parsedTimeStart.hour();
      startMinute = parsedTimeStart.minute();
      endHour = parsedTimeEnd.hour();
      return startHour <= 1 && startMinute == 0 && endHour <= 4;
    });

    if (untilMidnightEndIdx > -1 && until4amIdx > -1) {
      const copiedNext = [...next];
      const removedFoundedNext = copiedNext.splice(until4amIdx, 1);
      const copiedCurrent = [...current];
      copiedCurrent[untilMidnightEndIdx] = {
        ...copiedCurrent[untilMidnightEndIdx],
        actualEnd: removedFoundedNext[0].end_utc,
      };

      formattedShiftsValues[currentIdx] = copiedCurrent;
      formattedShiftsValues[nextIdx] = copiedNext;

      sortedWorkingHours[currentDay] = copiedCurrent;
      sortedWorkingHours[nextDay] = copiedNext;
    }
  }

  return (
    <div className="">
      <div
        className={cn(
          'mb-2 flex h-[108px] items-center justify-center space-x-1 rounded-[10px] text-h4 font-bold',
          isOpen ? 'bg-[#24C03014]' : '',
          !isOpen && !isOpeningSoon ? 'bg-[#ef000014]' : '',
          isOpeningSoon ? 'bg-[rgba(245,205,71,0.1)]' : ''
        )}
      >
        <If condition={isOpen}>
          <SvgCheck color="green" />
          <span>{t('storefront.store_profile.open')}</span>
        </If>
        <If condition={!isOpen && !isOpeningSoon}>
          <Close color="red" />
          <span>{t('storefront.store_profile.currently_closed')}</span>
        </If>
        <If condition={isOpeningSoon}>
          <SvgClock color="#646464" />
          <span>{t('storefront.store_profile.opening_soon')}</span>
        </If>
      </div>

      <ul className="space-y-2">
        {Object.keys(sortedWorkingHours).map((day, idx) => (
          <li
            key={day}
            className={cn(
              'flex items-center justify-between text-h7 leading-[150%] text-[#646464]',
              idx > 1 && !isExpaned ? 'hidden' : 'visible'
            )}
          >
            <span className="self-start">{t(day)}</span>
            <div className="flex flex-col space-y-2">
              {sortedWorkingHours[day].length === 0 &&
                isPaidSubscriber(activeSubscription) && (
                  <span>{t('common.closed')}</span>
                )}
              {sortedWorkingHours[day].length === 0 &&
                !isPaidSubscriber(activeSubscription) && (
                  <span>{t('common.closed')}</span>
                )}
              {sortedWorkingHours[day].map((shift, idx) => (
                <span
                  className="flex items-center gap-x-[8px]"
                  key={day + idx + 'shift'}
                >
                  <SvgClock />
                  <span>{formattedTime(shift.start_utc)}</span>-
                  <span>{formattedTime(shift.actualEnd ?? shift.end_utc)}</span>
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
      {isExpaned ? (
        <ChevronUpIcon
          className="h-6 w-6 cursor-pointer text-gray-400 hover:text-primary"
          onClick={() => setIsExpanded((prevState) => !prevState)}
        />
      ) : (
        <ChevronDownIcon
          className="h-6 w-6 cursor-pointer text-gray-400 hover:text-primary"
          onClick={() => setIsExpanded((prevState) => !prevState)}
        />
      )}
    </div>
  );
};
